import React from 'react';
import { ROLE_TINT } from '../data/roles';

// Style: dark figure (#0F0F0F) on tinted ground, tint-colored detail strokes.
// 320×320 viewBox. Each portrait shares the same vocabulary; the differentiator
// is hair / eyewear / facial hair / one accessory.

function PortraitFrame({ tint, children, label }) {
  return (
    <svg
      viewBox="0 0 320 320"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={label}
      style={{ display: 'block', width: '100%', height: '100%' }}
    >
      <rect width="320" height="320" fill={tint} />
      {children}
    </svg>
  );
}

const Neck = () => <rect x="130" y="226" width="60" height="60" fill="#0F0F0F" />;
const Shoulders = () => (
  <path d="M40 320 C 60 250, 110 240, 160 240 C 210 240, 260 250, 280 320 Z" fill="#0F0F0F" />
);
const SuitCollar = ({ tint }) => (
  <>
    <path d="M120 245 L160 280 L200 245 L200 320 L120 320 Z" fill={tint} />
    <path d="M120 245 L160 280 L200 245" stroke="#0F0F0F" strokeWidth="4" fill="none" />
  </>
);

// ── Engineers ────────────────────────────────────────────────────────────

// Priya — round head, square glasses, hoodie strings (the original engineer)
function PEngineerPriya({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Priya — Staff Engineer">
      <Shoulders />
      <line x1="148" y1="248" x2="144" y2="295" stroke={tint} strokeWidth="6" />
      <line x1="172" y1="248" x2="176" y2="295" stroke={tint} strokeWidth="6" />
      <Neck />
      <circle cx="160" cy="160" r="80" fill="#0F0F0F" />
      <rect x="108" y="148" width="40" height="32" rx="3" fill="none" stroke={tint} strokeWidth="6" />
      <rect x="172" y="148" width="40" height="32" rx="3" fill="none" stroke={tint} strokeWidth="6" />
      <line x1="148" y1="164" x2="172" y2="164" stroke={tint} strokeWidth="6" />
      <line x1="142" y1="206" x2="178" y2="206" stroke={tint} strokeWidth="5" strokeLinecap="round" />
      <path d="M88 132 Q120 84 200 84 Q236 84 244 116 L240 132 Q200 100 160 100 Q120 100 96 140 Z" fill="#0F0F0F" />
    </PortraitFrame>
  );
}

// Marco — engineering manager, stubble + clean fade
function PEngineerMarco({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Marco — Engineering Manager">
      <Shoulders />
      <Neck />
      <circle cx="160" cy="158" r="80" fill="#0F0F0F" />
      {/* short fade hair */}
      <path d="M84 150 Q84 78 160 78 Q236 78 236 150 L228 130 Q200 100 160 100 Q120 100 92 130 Z" fill="#0F0F0F" />
      {/* stubble — speckle */}
      <circle cx="120" cy="208" r="2" fill={tint} />
      <circle cx="135" cy="214" r="2" fill={tint} />
      <circle cx="155" cy="216" r="2" fill={tint} />
      <circle cx="175" cy="216" r="2" fill={tint} />
      <circle cx="195" cy="212" r="2" fill={tint} />
      <circle cx="128" cy="220" r="2" fill={tint} />
      <circle cx="148" cy="222" r="2" fill={tint} />
      <circle cx="168" cy="222" r="2" fill={tint} />
      <circle cx="188" cy="220" r="2" fill={tint} />
      {/* eyes */}
      <circle cx="132" cy="166" r="5" fill={tint} />
      <circle cx="188" cy="166" r="5" fill={tint} />
      {/* heavy brows */}
      <path d="M118 148 L146 146" stroke={tint} strokeWidth="6" strokeLinecap="round" />
      <path d="M174 146 L202 148" stroke={tint} strokeWidth="6" strokeLinecap="round" />
      {/* mouth */}
      <line x1="146" y1="200" x2="174" y2="200" stroke={tint} strokeWidth="5" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// Diego — long hair, full beard, no glasses
function PEngineerDiego({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Diego — Senior Engineer">
      <Shoulders />
      <Neck />
      {/* long hair behind */}
      <path d="M70 240 L70 130 Q70 70 160 70 Q250 70 250 130 L250 240 L210 240 L210 150 Q210 110 160 110 Q110 110 110 150 L110 240 Z" fill="#0F0F0F" />
      <circle cx="160" cy="170" r="74" fill="#0F0F0F" />
      {/* beard */}
      <path d="M104 196 Q104 240 160 250 Q216 240 216 196 Q216 220 188 224 Q160 226 132 224 Q104 220 104 196 Z" fill="#0F0F0F" />
      {/* eyes */}
      <circle cx="134" cy="168" r="5" fill={tint} />
      <circle cx="186" cy="168" r="5" fill={tint} />
      {/* nose */}
      <path d="M158 180 L156 196 L162 196" stroke={tint} strokeWidth="3" fill="none" strokeLinecap="round" />
      {/* mouth peeking from beard */}
      <path d="M148 210 Q160 216 172 210" stroke={tint} strokeWidth="4" fill="none" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// Yusuf — beanie, full short beard, frontend lead
function PEngineerYusuf({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Yusuf — Frontend Lead">
      <Shoulders />
      <Neck />
      <circle cx="160" cy="166" r="78" fill="#0F0F0F" />
      {/* beanie */}
      <path d="M84 140 Q84 76 160 76 Q236 76 236 140 L84 140 Z" fill="#0F0F0F" />
      <line x1="84" y1="140" x2="236" y2="140" stroke={tint} strokeWidth="6" />
      <path d="M120 76 Q120 60 160 60 Q200 60 200 76" stroke="#0F0F0F" strokeWidth="6" fill="none" />
      {/* beard line */}
      <path d="M108 198 Q108 232 160 240 Q212 232 212 198" stroke="#0F0F0F" strokeWidth="2" fill="none" />
      <path d="M108 198 Q140 224 160 224 Q180 224 212 198 Q212 230 160 240 Q108 230 108 198 Z" fill="#0F0F0F" />
      {/* eyes */}
      <circle cx="132" cy="174" r="5" fill={tint} />
      <circle cx="188" cy="174" r="5" fill={tint} />
      {/* mouth */}
      <line x1="148" y1="212" x2="172" y2="212" stroke={tint} strokeWidth="4" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// ── Designers ────────────────────────────────────────────────────────────

// Rita — bob haircut + earrings (the original designer)
function PDesignerRita({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Rita — Senior Designer">
      <Shoulders />
      <Neck />
      <path d="M70 230 L70 130 Q70 70 160 70 Q250 70 250 130 L250 230 L220 230 L220 150 Q220 110 160 110 Q100 110 100 150 L100 230 Z" fill="#0F0F0F" />
      <circle cx="160" cy="170" r="68" fill="#0F0F0F" />
      <path d="M100 142 Q160 110 220 142 L218 132 Q160 100 102 132 Z" fill="#0F0F0F" />
      <circle cx="92" cy="186" r="9" fill={tint} />
      <circle cx="228" cy="186" r="9" fill={tint} />
      <path d="M126 168 Q138 174 150 168" stroke={tint} strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M170 168 Q182 174 194 168" stroke={tint} strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M146 204 Q160 214 174 204" stroke={tint} strokeWidth="5" fill="none" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// Theo — short curls, single hoop earring, tucked-in feel
function PDesignerTheo({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Theo — Product Designer">
      <Shoulders />
      <Neck />
      <circle cx="160" cy="160" r="78" fill="#0F0F0F" />
      {/* curls cluster */}
      <circle cx="100" cy="116" r="14" fill="#0F0F0F" />
      <circle cx="120" cy="100" r="14" fill="#0F0F0F" />
      <circle cx="142" cy="92"  r="14" fill="#0F0F0F" />
      <circle cx="166" cy="88"  r="14" fill="#0F0F0F" />
      <circle cx="190" cy="92"  r="14" fill="#0F0F0F" />
      <circle cx="212" cy="100" r="14" fill="#0F0F0F" />
      <circle cx="228" cy="116" r="14" fill="#0F0F0F" />
      {/* one hoop earring */}
      <circle cx="86" cy="186" r="10" fill="none" stroke={tint} strokeWidth="4" />
      {/* eyes */}
      <circle cx="132" cy="170" r="5" fill={tint} />
      <circle cx="188" cy="170" r="5" fill={tint} />
      {/* mouth */}
      <path d="M144 206 Q160 218 176 206" stroke={tint} strokeWidth="5" fill="none" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// Nina — high bun, oval reading glasses (researcher)
function PDesignerNina({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Nina — UX Researcher">
      <Shoulders />
      <Neck />
      <circle cx="160" cy="166" r="74" fill="#0F0F0F" />
      {/* hair pulled back */}
      <path d="M86 144 Q92 80 160 80 Q228 80 234 144 Q200 116 160 116 Q120 116 86 144 Z" fill="#0F0F0F" />
      {/* high bun */}
      <circle cx="160" cy="56" r="22" fill="#0F0F0F" />
      <line x1="160" y1="78" x2="160" y2="92" stroke="#0F0F0F" strokeWidth="6" />
      {/* oval glasses */}
      <ellipse cx="130" cy="172" rx="20" ry="14" fill="none" stroke={tint} strokeWidth="5" />
      <ellipse cx="190" cy="172" rx="20" ry="14" fill="none" stroke={tint} strokeWidth="5" />
      <line x1="150" y1="172" x2="170" y2="172" stroke={tint} strokeWidth="4" />
      {/* eyes inside */}
      <circle cx="130" cy="172" r="3" fill={tint} />
      <circle cx="190" cy="172" r="3" fill={tint} />
      {/* mouth */}
      <line x1="148" y1="208" x2="172" y2="208" stroke={tint} strokeWidth="4" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// ── Execs ────────────────────────────────────────────────────────────────

// Karen — squarer head, slicked hair (the original exec)
function PExecKaren({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Karen — VP Product">
      <Shoulders />
      <SuitCollar tint={tint} />
      <Neck />
      <path d="M88 152 Q88 76 160 76 Q232 76 232 152 L232 198 Q232 240 160 240 Q88 240 88 198 Z" fill="#0F0F0F" />
      <path d="M88 132 Q120 76 160 76 Q200 76 232 132 L222 124 Q190 92 160 92 Q130 92 98 124 Z" fill={tint} opacity="0.85" />
      <line x1="118" y1="170" x2="142" y2="170" stroke={tint} strokeWidth="6" strokeLinecap="round" />
      <line x1="178" y1="170" x2="202" y2="170" stroke={tint} strokeWidth="6" strokeLinecap="round" />
      <line x1="140" y1="208" x2="180" y2="208" stroke={tint} strokeWidth="5" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// Tom — CEO, short cropped hair, square jaw, no nonsense
function PExecTom({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Tom — CEO">
      <Shoulders />
      <SuitCollar tint={tint} />
      <Neck />
      {/* squarer head */}
      <path d="M88 156 Q88 82 160 82 Q232 82 232 156 L232 196 Q232 240 160 240 Q88 240 88 196 Z" fill="#0F0F0F" />
      {/* very short hair (just the top edge) */}
      <path d="M84 138 Q84 80 160 80 Q236 80 236 138 L226 130 Q200 110 160 110 Q120 110 94 130 Z" fill="#0F0F0F" />
      {/* tiny tint highlight */}
      <path d="M104 122 Q140 100 160 100 Q180 100 216 122" stroke={tint} strokeWidth="3" fill="none" opacity="0.5" />
      {/* eyes — narrow */}
      <line x1="120" y1="170" x2="142" y2="170" stroke={tint} strokeWidth="6" strokeLinecap="round" />
      <line x1="178" y1="170" x2="200" y2="170" stroke={tint} strokeWidth="6" strokeLinecap="round" />
      {/* brows */}
      <path d="M116 152 L146 150" stroke={tint} strokeWidth="5" strokeLinecap="round" />
      <path d="M174 150 L204 152" stroke={tint} strokeWidth="5" strokeLinecap="round" />
      {/* flat mouth */}
      <line x1="138" y1="210" x2="182" y2="210" stroke={tint} strokeWidth="5" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// Marcus — CRO, side-part + mustache
function PExecMarcus({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Marcus — CRO">
      <Shoulders />
      <SuitCollar tint={tint} />
      <Neck />
      <circle cx="160" cy="160" r="78" fill="#0F0F0F" />
      {/* side-part hair */}
      <path d="M84 130 Q90 76 160 76 Q230 76 236 130 L222 130 Q200 96 158 100 Q126 104 100 122 Q90 128 84 138 Z" fill="#0F0F0F" />
      <line x1="138" y1="78" x2="120" y2="118" stroke={tint} strokeWidth="3" opacity="0.5" />
      {/* mustache */}
      <path d="M134 200 Q150 192 160 198 Q170 192 186 200 Q170 206 160 204 Q150 206 134 200 Z" fill={tint} />
      {/* eyes */}
      <circle cx="132" cy="170" r="5" fill={tint} />
      <circle cx="188" cy="170" r="5" fill={tint} />
      {/* mouth below mustache */}
      <line x1="148" y1="216" x2="172" y2="216" stroke={tint} strokeWidth="4" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// Sandra — CFO, shoulder-length hair, stud earrings
function PExecSandra({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Sandra — CFO">
      <Shoulders />
      <SuitCollar tint={tint} />
      <Neck />
      {/* shoulder-length hair */}
      <path d="M76 240 L76 130 Q76 70 160 70 Q244 70 244 130 L244 240 L214 240 L214 154 Q214 110 160 110 Q106 110 106 154 L106 240 Z" fill="#0F0F0F" />
      <circle cx="160" cy="166" r="68" fill="#0F0F0F" />
      {/* fringe sweep */}
      <path d="M96 138 Q140 108 220 134 L218 124 Q160 96 100 124 Z" fill="#0F0F0F" />
      {/* stud earrings */}
      <circle cx="92" cy="184" r="4" fill={tint} />
      <circle cx="228" cy="184" r="4" fill={tint} />
      {/* eyes */}
      <line x1="120" y1="170" x2="142" y2="170" stroke={tint} strokeWidth="5" strokeLinecap="round" />
      <line x1="178" y1="170" x2="200" y2="170" stroke={tint} strokeWidth="5" strokeLinecap="round" />
      {/* lips */}
      <path d="M146 206 Q160 214 174 206" stroke={tint} strokeWidth="5" fill="none" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// ── Customers ────────────────────────────────────────────────────────────

// Eve — power user (the original customer)
function PCustomerEve({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Eve — Power User">
      <Shoulders />
      <Neck />
      <circle cx="160" cy="160" r="80" fill="#0F0F0F" />
      <path d="M86 140 Q100 80 160 80 Q220 80 234 140 Q190 110 160 110 Q130 110 86 140 Z" fill="#0F0F0F" />
      <circle cx="132" cy="166" r="6" fill={tint} />
      <circle cx="188" cy="166" r="6" fill={tint} />
      <path d="M118 148 L146 144" stroke={tint} strokeWidth="5" strokeLinecap="round" />
      <path d="M174 144 L202 148" stroke={tint} strokeWidth="5" strokeLinecap="round" />
      <path d="M148 208 Q160 200 172 208" stroke={tint} strokeWidth="5" fill="none" strokeLinecap="round" />
      <rect x="220" y="248" width="44" height="56" rx="3" fill="#0F0F0F" />
      <path d="M264 260 Q280 262 280 280 Q280 296 264 296" stroke="#0F0F0F" strokeWidth="6" fill="none" />
      <rect x="226" y="246" width="32" height="6" fill={tint} />
    </PortraitFrame>
  );
}

// BigCo IT — generic enterprise IT contact, polo collar + lanyard badge
function PCustomerBigCo({ tint }) {
  return (
    <PortraitFrame tint={tint} label="BigCo IT contact">
      <Shoulders />
      {/* polo collar */}
      <path d="M130 248 L160 270 L190 248 L196 280 L124 280 Z" fill={tint} />
      <line x1="160" y1="248" x2="160" y2="278" stroke="#0F0F0F" strokeWidth="3" />
      {/* lanyard line */}
      <path d="M132 248 L154 290 L168 290 L188 248" stroke="#0F0F0F" strokeWidth="3" fill="none" />
      <rect x="148" y="290" width="24" height="28" rx="2" fill="#0F0F0F" stroke={tint} strokeWidth="2" />
      <Neck />
      <circle cx="160" cy="160" r="78" fill="#0F0F0F" />
      {/* receding hair */}
      <path d="M92 134 Q120 90 160 90 Q200 90 228 134 L218 130 Q190 110 160 110 Q130 110 102 130 Z" fill="#0F0F0F" />
      <path d="M124 90 Q160 80 196 90" stroke={tint} strokeWidth="3" opacity="0.4" fill="none" />
      {/* square glasses */}
      <rect x="106" y="154" width="40" height="28" rx="3" fill="none" stroke={tint} strokeWidth="5" />
      <rect x="174" y="154" width="40" height="28" rx="3" fill="none" stroke={tint} strokeWidth="5" />
      <line x1="146" y1="168" x2="174" y2="168" stroke={tint} strokeWidth="4" />
      {/* mouth */}
      <line x1="142" y1="210" x2="178" y2="210" stroke={tint} strokeWidth="4" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// ── Sales ────────────────────────────────────────────────────────────────

// Jules — headset, big grin (the original sales)
function PSalesJules({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Jules — Enterprise AE">
      <Shoulders />
      <SuitCollar tint={tint} />
      <Neck />
      <circle cx="160" cy="160" r="80" fill="#0F0F0F" />
      <path d="M86 130 Q108 80 160 80 Q210 80 234 130 L222 134 Q210 100 174 100 Q142 100 130 116 Q120 124 96 138 Z" fill="#0F0F0F" />
      <path d="M80 160 Q80 90 160 90 Q240 90 240 160" stroke={tint} strokeWidth="6" fill="none" />
      <rect x="74" y="156" width="14" height="24" rx="3" fill={tint} />
      <path d="M236 178 L232 200 L222 200" stroke={tint} strokeWidth="6" fill="none" strokeLinecap="round" />
      <circle cx="220" cy="200" r="6" fill={tint} />
      <path d="M124 168 Q132 158 142 168" stroke={tint} strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M178 168 Q186 158 196 168" stroke={tint} strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M132 198 Q160 224 188 198" stroke={tint} strokeWidth="5" fill="none" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// Gabe — beard, AirPods (one visible), softer smile
function PSalesGabe({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Gabe — Account Executive">
      <Shoulders />
      <SuitCollar tint={tint} />
      <Neck />
      <circle cx="160" cy="158" r="78" fill="#0F0F0F" />
      {/* hair short, side */}
      <path d="M86 132 Q100 82 160 82 Q224 82 236 134 Q204 102 160 100 Q120 98 88 134 Z" fill="#0F0F0F" />
      {/* trimmed beard */}
      <path d="M114 198 Q114 232 160 240 Q206 232 206 198 Q190 218 160 220 Q130 218 114 198 Z" fill="#0F0F0F" />
      {/* AirPod (right ear) */}
      <rect x="222" y="168" width="10" height="22" rx="3" fill={tint} />
      <circle cx="227" cy="170" r="6" fill={tint} />
      {/* eyes */}
      <path d="M124 170 Q132 162 142 170" stroke={tint} strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M178 170 Q186 162 196 170" stroke={tint} strokeWidth="5" fill="none" strokeLinecap="round" />
      {/* mouth */}
      <path d="M142 212 Q160 222 178 212" stroke={tint} strokeWidth="4" fill="none" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// ── Data ─────────────────────────────────────────────────────────────────

// Ash — round glasses, scattered data points (the original data scientist)
function PDataAsh({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Ash — Data Scientist">
      <Shoulders />
      <Neck />
      <circle cx="160" cy="160" r="80" fill="#0F0F0F" />
      <path d="M88 134 Q108 70 160 70 Q212 70 232 134 Q200 90 160 96 Q124 102 88 134 Z" fill="#0F0F0F" />
      <path d="M212 84 Q220 76 230 84" stroke="#0F0F0F" strokeWidth="6" fill="none" strokeLinecap="round" />
      <circle cx="130" cy="168" r="20" fill="none" stroke={tint} strokeWidth="6" />
      <circle cx="190" cy="168" r="20" fill="none" stroke={tint} strokeWidth="6" />
      <line x1="150" y1="168" x2="170" y2="168" stroke={tint} strokeWidth="6" />
      <circle cx="130" cy="168" r="3" fill={tint} />
      <circle cx="190" cy="168" r="3" fill={tint} />
      <line x1="150" y1="208" x2="170" y2="208" stroke={tint} strokeWidth="5" strokeLinecap="round" />
      <circle cx="50" cy="60" r="5" fill="#0F0F0F" />
      <circle cx="80" cy="40" r="3" fill="#0F0F0F" />
      <circle cx="270" cy="50" r="4" fill="#0F0F0F" />
      <circle cx="40" cy="100" r="3" fill="#0F0F0F" />
      <circle cx="280" cy="90" r="6" fill="#0F0F0F" />
    </PortraitFrame>
  );
}

// Lin — straight long hair, rectangular glasses, no chaos
function PDataLin({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Lin — Senior Data Scientist">
      <Shoulders />
      <Neck />
      {/* long straight hair */}
      <path d="M76 240 L76 122 Q76 66 160 66 Q244 66 244 122 L244 240 L210 240 L210 142 Q210 102 160 102 Q110 102 110 142 L110 240 Z" fill="#0F0F0F" />
      <circle cx="160" cy="166" r="68" fill="#0F0F0F" />
      {/* straight fringe */}
      <path d="M100 130 L220 130 L218 122 Q160 100 102 122 Z" fill="#0F0F0F" />
      {/* rectangular glasses */}
      <rect x="108" y="156" width="40" height="22" rx="2" fill="none" stroke={tint} strokeWidth="5" />
      <rect x="172" y="156" width="40" height="22" rx="2" fill="none" stroke={tint} strokeWidth="5" />
      <line x1="148" y1="166" x2="172" y2="166" stroke={tint} strokeWidth="4" />
      <circle cx="128" cy="167" r="3" fill={tint} />
      <circle cx="192" cy="167" r="3" fill={tint} />
      {/* mouth */}
      <line x1="150" y1="208" x2="170" y2="208" stroke={tint} strokeWidth="4" strokeLinecap="round" />
      {/* tiny chart in corner */}
      <path d="M30 280 L30 300 L50 300 M30 296 L36 290 L42 294 L48 286" stroke="#0F0F0F" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </PortraitFrame>
  );
}

// ── Intern ───────────────────────────────────────────────────────────────

// Wren — beanie, freckles
function PInternWren({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Wren — Product Intern">
      <Shoulders />
      <Neck />
      <circle cx="160" cy="170" r="76" fill="#0F0F0F" />
      <path d="M88 132 Q88 70 160 70 Q232 70 232 132 L88 132 Z" fill="#0F0F0F" />
      <line x1="88" y1="132" x2="232" y2="132" stroke={tint} strokeWidth="8" />
      <circle cx="160" cy="60" r="8" fill="#0F0F0F" />
      <circle cx="130" cy="172" r="6" fill={tint} />
      <circle cx="190" cy="172" r="6" fill={tint} />
      <path d="M140 210 Q160 222 180 210" stroke={tint} strokeWidth="5" fill="none" strokeLinecap="round" />
      <circle cx="118" cy="196" r="2" fill={tint} />
      <circle cx="202" cy="196" r="2" fill={tint} />
    </PortraitFrame>
  );
}

// ── Recruiter ────────────────────────────────────────────────────────────

// Sam — sharp ponytail, lanyard
function PRecruiterSam({ tint }) {
  return (
    <PortraitFrame tint={tint} label="Sam — Tech Recruiter">
      <Shoulders />
      <Neck />
      <path d="M132 240 L160 280 L188 240" stroke="#0F0F0F" strokeWidth="6" fill="none" />
      <rect x="146" y="276" width="28" height="34" rx="3" fill="#0F0F0F" stroke={tint} strokeWidth="3" />
      <Neck />
      <path d="M84 152 Q84 76 160 76 Q236 76 236 152 L236 200 Q236 240 160 240 Q84 240 84 200 Z" fill="#0F0F0F" />
      <path d="M232 132 Q288 130 280 200 Q272 232 244 220" fill="#0F0F0F" />
      <path d="M84 138 Q120 92 160 92 Q210 92 236 138 L222 130 Q200 116 160 116 Q120 116 96 132 Z" fill={tint} opacity="0.9" />
      <line x1="118" y1="170" x2="138" y2="170" stroke={tint} strokeWidth="6" strokeLinecap="round" />
      <line x1="182" y1="170" x2="202" y2="170" stroke={tint} strokeWidth="6" strokeLinecap="round" />
      <path d="M144 208 Q160 216 176 208" stroke={tint} strokeWidth="5" fill="none" strokeLinecap="round" />
    </PortraitFrame>
  );
}

// ── Dispatch ─────────────────────────────────────────────────────────────

const PORTRAIT_BY_NAME = {
  // engineers
  'Priya': PEngineerPriya,
  'Marco': PEngineerMarco,
  'Diego': PEngineerDiego,
  'Yusuf': PEngineerYusuf,
  // designers
  'Rita': PDesignerRita,
  'Theo': PDesignerTheo,
  'Nina': PDesignerNina,
  // execs
  'Karen': PExecKaren,
  'Tom': PExecTom,
  'Marcus': PExecMarcus,
  'Sandra': PExecSandra,
  // customers
  'Eve': PCustomerEve,
  'BigCo IT': PCustomerBigCo,
  // sales
  'Jules': PSalesJules,
  'Gabe': PSalesGabe,
  // data
  'Ash': PDataAsh,
  'Lin': PDataLin,
  // intern
  'Wren': PInternWren,
  // recruiter
  'Sam': PRecruiterSam,
};

const PORTRAIT_BY_ROLE = {
  engineer: PEngineerPriya,
  designer: PDesignerRita,
  exec: PExecKaren,
  customer: PCustomerEve,
  sales: PSalesJules,
  data: PDataAsh,
  intern: PInternWren,
  recruiter: PRecruiterSam,
};

export default function Portrait({ role, name }) {
  const C = PORTRAIT_BY_NAME[name] || PORTRAIT_BY_ROLE[role] || PEngineerPriya;
  return <C tint={ROLE_TINT[role] || '#EFF2FF'} />;
}
